import type { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { TaskType } from "@common/types/task";
import type {
    PickerItemData,
    PickerSectionData,
} from "@design-system/Picker/Picker.types";
import {
    resolveTaskTypeFromOption,
    type TaskTypeOption,
} from "./closeTemplateTasksDrawer.constants";
import {
    toOrgTaskTemplatePickerValue,
    type OrgTaskTemplatePickerValue,
} from "./orgTaskTemplatePicker.utils";

export const KICK_TEMPLATES_SECTION_LABEL = "Kick templates";
export const FIRM_TEMPLATES_SECTION_LABEL = "Firm templates";

export type WorkflowPickerValue =
    | TaskTypeOption
    | OrgTaskTemplatePickerValue
    | `__group__${string}`;

type WorkflowPickerItem = PickerItemData<WorkflowPickerValue>;

const FLYOUT_SEARCH_MIN_ITEMS = 7;

const toFlyoutOptions = (
    itemCount: number,
): Pick<WorkflowPickerItem, "flyout"> =>
    itemCount >= FLYOUT_SEARCH_MIN_ITEMS
        ? {
              flyout: {
                  searchable: true,
                  searchPlaceholder: "Search workflows…",
              },
          }
        : {};

interface WorkflowOptionGroup {
    label: string | null;
    options: ReadonlyArray<{ value: TaskTypeOption; label: string }>;
}

interface MainOption {
    label: string;
    isGroup: boolean;
    options: WorkflowOptionGroup["options"];
}

const toMainOptions = (groups: readonly WorkflowOptionGroup[]): MainOption[] =>
    groups.flatMap((group): MainOption[] =>
        group.label === null
            ? group.options.map((option) => ({
                  label: option.label,
                  isGroup: false,
                  options: [option],
              }))
            : [{ label: group.label, isGroup: true, options: group.options }],
    );

const findMainOptionIndexForTemplate = ({
    mainOptions,
    template,
}: {
    mainOptions: readonly MainOption[];
    template: OrganizationTaskTemplate;
}): number => {
    const templateType = template.type ?? TaskType.QUESTION;
    const findIndexForType = (type: TaskType) =>
        mainOptions.findIndex((mainOption) =>
            mainOption.options.some(
                (option) => resolveTaskTypeFromOption(option.value) === type,
            ),
        );
    const index = findIndexForType(templateType);
    return index === -1 ? findIndexForType(TaskType.QUESTION) : index;
};

const toOptionItem = (option: {
    value: TaskTypeOption;
    label: string;
}): WorkflowPickerItem => ({ value: option.value, label: option.label });

const toTemplateItem = (
    template: OrganizationTaskTemplate,
): WorkflowPickerItem => ({
    value: toOrgTaskTemplatePickerValue(template.id),
    label: template.title,
});

const toGroupValue = (label: string): WorkflowPickerValue =>
    `__group__${label}`;

/**
 * Builds the workflow picker's items. Every main option is a Kick workflow
 * (or group of them). Firm (org) task templates never become main options:
 * they are nested under the main option matching their task type, and that
 * option's flyout is split into "Firm templates" (listed first) and "Kick
 * templates" sections. Main options without firm templates keep their plain
 * layout.
 */
export const buildWorkflowPickerItems = ({
    groups,
    orgTaskTemplates,
}: {
    groups: readonly WorkflowOptionGroup[];
    orgTaskTemplates: readonly OrganizationTaskTemplate[];
}): WorkflowPickerItem[] => {
    const mainOptions = toMainOptions(groups).filter(
        (mainOption) => mainOption.options.length > 0,
    );
    const templatesByMainOption = new Map<number, OrganizationTaskTemplate[]>();
    for (const template of orgTaskTemplates) {
        const index = findMainOptionIndexForTemplate({ mainOptions, template });
        if (index !== -1) {
            templatesByMainOption.set(index, [
                ...(templatesByMainOption.get(index) ?? []),
                template,
            ]);
        }
    }

    return mainOptions.map((mainOption, index): WorkflowPickerItem => {
        const templates = templatesByMainOption.get(index) ?? [];
        const optionItems = mainOption.options.map(toOptionItem);

        if (templates.length > 0) {
            const sections: Array<PickerSectionData<WorkflowPickerValue>> = [
                {
                    title: FIRM_TEMPLATES_SECTION_LABEL,
                    items: templates.map(toTemplateItem),
                },
                { title: KICK_TEMPLATES_SECTION_LABEL, items: optionItems },
            ];
            return {
                value: toGroupValue(mainOption.label),
                label: mainOption.label,
                sections,
                ...toFlyoutOptions(optionItems.length + templates.length),
            };
        }

        const [onlyOption] = optionItems;
        if (!mainOption.isGroup && onlyOption) {
            return onlyOption;
        }
        return {
            value: toGroupValue(mainOption.label),
            label: mainOption.label,
            children: optionItems,
            ...toFlyoutOptions(optionItems.length),
        };
    });
};
